import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  User, 
  Settings, 
  LogOut, 
  ChevronDown,
  MessageSquare,
  Shield
} from 'lucide-react';
import { signOutUser } from '../../services/authService';
import toast from 'react-hot-toast';

const UserDropdown = ({ user, darkMode, onProfileClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSignOut = async () => {
    try {
      await signOutUser();
      setIsOpen(false);
      toast.success('Signed out successfully');
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Failed to sign out');
    }
  };

  const handleProfileClick = () => {
    onProfileClick();
    setIsOpen(false);
  };

  const getUserDisplayName = () => {
    if (user?.displayName) {
      return user.displayName;
    }
    if (user?.email) {
      return user.email.split('@')[0];
    }
    return 'User';
  };

  const getUserInitials = () => {
    const name = getUserDisplayName();
    return name.charAt(0).toUpperCase();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* User Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center space-x-2 p-2 rounded-lg transition-all duration-200 ${
          darkMode 
            ? 'text-white hover:bg-white/10' 
            : 'text-gray-700 hover:bg-black/10'
        } ${isOpen ? (darkMode ? 'bg-white/10' : 'bg-black/10') : ''}`}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-bold">
          {getUserInitials()}
        </div>
        <div className="hidden sm:block text-left">
          <div className="text-sm font-medium truncate max-w-24">
            {getUserDisplayName()}
          </div>
          <div className="text-xs opacity-75">
            {user?.emailVerified ? (
              <span className="text-green-500">Verified</span>
            ) : (
              <span className="text-yellow-500">Pending</span>
            )}
          </div>
        </div>
        <ChevronDown 
          className={`w-4 h-4 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`} 
        />
      </motion.button>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className={`absolute right-0 mt-2 w-64 rounded-lg shadow-lg border z-50 ${
              darkMode 
                ? 'bg-gray-800 border-gray-700' 
                : 'bg-white border-gray-200'
            }`}
          >
            {/* User Info Header */}
            <div className={`p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center text-white text-lg font-bold">
                  {getUserInitials()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">
                    {getUserDisplayName()}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {user?.email}
                  </div>
                  <div className="flex items-center space-x-2 mt-1">
                    <Shield className="w-3 h-3" />
                    <span className="text-xs">
                      {user?.emailVerified ? (
                        <span className="text-green-600">Email Verified</span>
                      ) : (
                        <span className="text-yellow-600">Email Pending</span>
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Menu Items */}
            <div className="py-2">
              <button
                onClick={handleProfileClick}
                className={`w-full flex items-center space-x-3 px-4 py-3 text-sm transition-colors ${
                  darkMode 
                    ? 'text-gray-300 hover:bg-gray-700 hover:text-white' 
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <User className="w-4 h-4" />
                <div className="text-left">
                  <div className="font-medium">Update Info</div>
                  <div className="text-xs opacity-75">Update profile information</div>
                </div>
              </button>

              <button
                onClick={handleProfileClick}
                className={`w-full flex items-center space-x-3 px-4 py-3 text-sm transition-colors ${
                  darkMode 
                    ? 'text-gray-300 hover:bg-gray-700 hover:text-white' 
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <MessageSquare className="w-4 h-4" />
                <div className="text-left">
                  <div className="font-medium">Chat History</div>
                  <div className="text-xs opacity-75">View past conversations</div>
                </div>
              </button>

              <button
                onClick={handleProfileClick}
                className={`w-full flex items-center space-x-3 px-4 py-3 text-sm transition-colors ${
                  darkMode 
                    ? 'text-gray-300 hover:bg-gray-700 hover:text-white' 
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <Settings className="w-4 h-4" />
                <div className="text-left">
                  <div className="font-medium">Account Settings</div>
                  <div className="text-xs opacity-75">Privacy and preferences</div>
                </div>
              </button>

              {/* Divider */}
              <div className={`my-2 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}></div>

              {/* Sign Out */}
              <button
                onClick={handleSignOut}
                className={`w-full flex items-center space-x-3 px-4 py-3 text-sm transition-colors ${
                  darkMode 
                    ? 'text-red-400 hover:bg-red-900/20 hover:text-red-300' 
                    : 'text-red-600 hover:bg-red-50 hover:text-red-700'
                }`}
              >
                <LogOut className="w-4 h-4" />
                <div className="text-left">
                  <div className="font-medium">Sign Out</div>
                  <div className="text-xs opacity-75">End your session</div>
                </div>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserDropdown; 